import React, { useCallback } from "react";
import { css, cx } from "@linaria/core";
import { styled } from "@linaria/react";
import {
  FullViewportButtonMarker,
  FullViewportButtonMarkerActive,
  FullViewportButtonMarkerInactive,
} from "@ui/FullViewportButton";
import { spacings } from "@ui/Theme";
import ChartCard from "@ui/Highcharts/ui/Card";
import FailureLegend from "../../Chart/Legend/FailureLegend";
import HighchartsComponent from "@ui/Highcharts";
import type { Project } from "../../../../api/useProjects";
import type { Source } from "../../../../api/useSources";
import { useQuantityIndex, type Quantity } from "../../../../api/useQuantities";
import type { Failure, Setup } from "../../../../api/useSetups";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { isFailureValueInFaultEventOccurrencePeriods } from "diagnostics-domain/faultEvent";
import { exportWidgetTarget } from "@ui/ExportWidgetButton";
import DiagnosisLegend from "../../Chart/Legend/DiagnosisLegend";
import CommonLegend from "../../Chart/Legend/CommonLegend";
import { useDiagnosticWithQuantityNoDataIntervals } from "../../useNoDataIntervals";
import { GLOBAL_DIAGNOSIS_CHART, useDiagnosisChartType } from "./hooks/useDiagnosisChartType";
import { Divider } from "@ui/Highcharts/ui/Tooltip";
import { getOtherSerieDescription } from ".";
import type { ManualFailure } from "../../../../api/useManualFailures";
import { useProjectPeriod } from "../../useProjectPeriod";
import useSeries from "./useSeries";
import useSuspectedFailuresInExplanation from "../useSuspectedFailuresInExplanation";
import { useCanUserDoOnProject } from "../../../canUserDo";
import { usePublicationFlagDisplay } from "../../Analysis/TogglePublicationDisplay";
import { useSettings } from "../../../../api/useSettings";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { Label } from "@ui/Highcharts/ui/Legend";
import useOptions from "./useOptions";
import useModal from "@ui/lib/hooks/useModal";
import FailureGroupsModal from "../../Chart/Legend/FailureLegend/FailureGroupsModal";
import { FailureLegendMenu, useLegendGroupClick } from "../../Chart/Legend/FailureLegend";
import { useFailureGroupDisplayNameMap } from "../../../../api/useFailureGroups";
import { useEvents, type Event as ProjectEvent } from "../../../../api/useEvents";
import { useBasename, toBasenamePath } from "../../../../router/helpers/navigation";
import { gotoEventPage } from "../../Events/utils/event";
import { useFaultEventAccess } from "../../Events/hooks/useFaultEventAccess";

const ChartLegendWrapper = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 1fr;
  height: 400px;
  width: 100%;

  .${FullViewportButtonMarkerActive} &,
  .${FullViewportButtonMarkerInactive} & {
    height: 100%;
  }
`;
const FailureEvolutionChartCard = css`
  row-gap: ${spacings.m};
  :not(.${FullViewportButtonMarker}) {
    max-height: 80vh;
  }
`;

interface P {
  title: React.JSX.Element;
  subtitle: React.JSX.Element;
  legendLeft?: React.ReactNode;

  project: Project;
  source: Source;
  quantities: Quantity[];
  setups: Setup[];
  activeAnalyses: ActiveAnalysis[];
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  analysisSetup: Setup | null;
}

const DiagnosisHighcharts = ({
  title,
  subtitle,
  legendLeft,
  project,
  source,
  quantities,
  setups,
  activeAnalyses,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  analysisSetup,
}: P) => {
  const settings = useSettings();
  const {
    modal: failureGroupsModal,
    openModal: openFailureGroupsModal,
    closeModal: closeFailureGroupsModal,
  } = useModal();
  const faultEventAccess = useFaultEventAccess();
  const chartRef = React.useRef<Highcharts.Chart>(null);
  const chartRefs = React.useMemo(() => [chartRef], [chartRef]);
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });
  const { chartType, chartTypeOption } = useDiagnosisChartType({
    project,
    quantities,
  });
  const [suspectedFailuresIncluded] = useSuspectedFailuresInExplanation({ scope: project.id });
  const events = useEvents(project.id, settings.MANUAL_EVENTS_DEV_MODE_ENABLED);
  const basename = useBasename();
  const getEventForPoint = useCallback(
    (failureName: string | null, pointDate: number, isManual: boolean) => {
      if (!faultEventAccess) return null;
      if (isManual && !settings.MANUAL_EVENTS_DEV_MODE_ENABLED) return null;
      if (!failureName) return null;

      const event = events.find(candidate => {
        if (isManual && candidate.type !== "MANUAL") return false;
        if (!isManual && candidate.type !== "DIAGNOSED") return false;

        const hasMatchingFailure =
          candidate.type === "DIAGNOSED"
            ? candidate.metadata.faults.some(fault => fault.name === failureName)
            : (candidate.metadata.manualFaultName ?? candidate.metadata.manualFault.name) ===
              failureName;
        if (!hasMatchingFailure) return false;

        if (candidate.type === "DIAGNOSED") {
          return isFailureValueInFaultEventOccurrencePeriods(candidate, {
            projectId: candidate.projectId,
            failureName,
            date: pointDate,
          });
        }

        return (
          candidate.startsAt.getTime() <= pointDate &&
          pointDate <= candidate.metadata.endsAt.getTime()
        );
      });

      return event ?? null;
    },
    [events, faultEventAccess, settings.MANUAL_EVENTS_DEV_MODE_ENABLED],
  );
  const getEventLink = useCallback(
    (event: ProjectEvent) =>
      toBasenamePath(gotoEventPage(event), basename ?? window.__webpack_public_path__),
    [basename],
  );

  const [period] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });
  const quantityIndex = useQuantityIndex(project.id);

  const qoi = React.useMemo(() => {
    if (!chartType || chartType === GLOBAL_DIAGNOSIS_CHART) return null;
    return quantityIndex[chartType] ?? null;
  }, [chartType, quantityIndex]);
  const yAxisId = `project-id-${project.id}-${
    qoi === null ? GLOBAL_DIAGNOSIS_CHART : `qoi-id-${qoi["id"]}`
  }`;
  const failureGroupingEnabled = settings.FAILURE_GROUPING_ENABLED;
  const { groupByFailureDisplayName } = useFailureGroupDisplayNameMap(project.id, {
    enabled: failureGroupingEnabled,
  });
  const enabledFailureDisplayNames = React.useMemo(() => {
    const names = new Set<string>();
    for (const setup of setups) {
      for (const failure of setup["failures"]) {
        if (!failure.enabled) continue;
        const displayName = failure.displayName ?? failure.name;
        if (displayName) names.add(displayName);
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [setups]);

  const legendHeaderMenu = React.useMemo(
    () => (canUserWriteProject ? <FailureLegendMenu onClick={openFailureGroupsModal} /> : null),
    [canUserWriteProject, openFailureGroupsModal],
  );
  const series = useSeries({
    project,
    yAxisId,
    setups,
    analyses: activeAnalyses,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    qoi,
    includeSuspectedFailure: suspectedFailuresIncluded,
    quantityIndex,
    actionAllowed: canUserWriteProject,
    publicationFlagDisplayed: Boolean(publicationFlagDisplayed),
    displayImpact:
      chartType === GLOBAL_DIAGNOSIS_CHART ? !settings.PROJECT_HIDDEN_GLOBAL_IMPACT : true,
  });
  const [visibleSeries, onSerieClick, setSerieVisibility] = useVisibleSeries(
    `diagnosisOverview-${project.id}`,
    series,
  );
  const handleLegendGroupClick = useLegendGroupClick(visibleSeries, setSerieVisibility);

  const renderLabel = React.useCallback(
    (
      _failure: Failure | ManualFailure | undefined,
      serie:
        | Highcharts.SeriesLineOptions
        | Highcharts.SeriesArearangeOptions
        | Highcharts.SeriesAreaOptions
        | Highcharts.SeriesScatterOptions,
    ) => {
      const failureDisplayName =
        typeof serie.custom === "object" &&
        serie.custom !== null &&
        "failureDisplayName" in serie.custom
          ? serie.custom["failureDisplayName"]
          : serie.name;

      return <Label>{failureDisplayName}</Label>;
    },
    [],
  );

  const options = useOptions({
    source,
    project,
    chartRef,
    chartTypeOption,
    yAxisId,
    analyses: activeAnalyses,
    indexedFailureValues,
    qoi,
    series: visibleSeries,
    getEventForPoint,
    getEventLink,
  });

  const renderOtherSeriesTooltip = React.useCallback(
    (serie: Highcharts.SeriesOptions) => {
      const serieId = serie.id;

      return (
        <>
          <Divider />
          {getOtherSerieDescription({
            serieId,
            chartType: chartType ?? GLOBAL_DIAGNOSIS_CHART,
            suspectedFailuresIncluded,
          })}
        </>
      );
    },
    [chartType, suspectedFailuresIncluded],
  );

  const noDataIntervals = useDiagnosticWithQuantityNoDataIntervals(
    activeAnalyses,
    indexedFailureValues,
  );

  const legend = React.useMemo(
    () => (
      <>
        <DiagnosisLegend chartRef={chartRef} series={visibleSeries} period={period} />
        <CommonLegend
          chartRef={chartRef}
          analyses={activeAnalyses}
          series={visibleSeries}
          period={period}
          noDataIntervals={noDataIntervals}
          chartType="diagnosis"
          projectId={project.id}
        />
      </>
    ),
    [activeAnalyses, period, visibleSeries, noDataIntervals, project],
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legendLeft={legendLeft}
      legend={legend}
      className={cx(FailureEvolutionChartCard, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <ChartLegendWrapper>
        <FailureLegend
          project={project}
          chartRefs={chartRefs}
          series={visibleSeries}
          period={period}
          onLegendItemClick={onSerieClick}
          setup={analysisSetup}
          renderOtherSeriesTooltip={renderOtherSeriesTooltip}
          renderLabel={renderLabel}
          headerMenu={failureGroupingEnabled ? legendHeaderMenu : null}
          groupByFailureDisplayName={failureGroupingEnabled ? groupByFailureDisplayName : undefined}
          onLegendGroupClick={handleLegendGroupClick}
        />
        <HighchartsComponent
          name="Diagnosis overview"
          ref={chartRef}
          options={options}
          selectablePoints
        />
      </ChartLegendWrapper>
      {failureGroupingEnabled ? (
        <FailureGroupsModal
          projectId={project.id}
          isOpen={failureGroupsModal}
          onRequestClose={closeFailureGroupsModal}
          enabledFailureDisplayNames={enabledFailureDisplayNames}
        />
      ) : null}
    </ChartCard>
  );
};

export default DiagnosisHighcharts;
